import { useState, useEffect, useCallback } from 'react';
import { insightsService, NBAResponseItem } from '@/services/insights.service';
import { getNBACTAConfig, NBACType } from '@/types/nba-mapping';

interface UseInsightsOptions {
  autoFetch?: boolean;
  sortByPriority?: boolean;
}

interface UseInsightsReturn {
  nbaItems: NBAResponseItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  getHighPriorityItems: () => NBAResponseItem[];
  getCTAConfig: (item: NBAResponseItem) => any;
  hasHighPriorityItems: boolean;
}

export const useInsights = (options: UseInsightsOptions = {}): UseInsightsReturn => {
  const { autoFetch = true, sortByPriority = true } = options;
  
  const [nbaItems, setNbaItems] = useState<NBAResponseItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchInsights = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const items: NBAResponseItem[] = await insightsService.getNextBestActions();
      
      let sortedItems = items;
      if (sortByPriority) {
        sortedItems = getSortedNBAItems(items);
      }
      
      // Remove duplicates based on ctaType - keep the first (highest priority) occurrence
      const uniqueItems = sortedItems.filter((item, index, self) => 
        index === self.findIndex((t) => t.ctaType === item.ctaType)
      );
      
      setNbaItems(uniqueItems);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch insights';
      setError(errorMessage);
      console.error('Error fetching insights:', err);
    } finally {
      setLoading(false);
    }
  }, [sortByPriority]);

  const getHighPriorityItems = useCallback(() => {
    return nbaItems; // Return all items, let component slice to first 3
  }, [nbaItems]);

  const getCTAConfig = useCallback((item: NBAResponseItem) => {
    // Cast string to NBACType, fallback to UPDATE_HEALTH_PROFILE if not found
    const ctaType = item.ctaType as NBACType;
    return getNBACTAConfig(ctaType, item.metadata);
  }, []);

  const hasHighPriorityItems = getHighPriorityItems().length > 0;

  useEffect(() => {
    if (autoFetch) {
      fetchInsights();
    }
  }, [autoFetch, fetchInsights]);

  return {
    nbaItems,
    loading,
    error,
    refetch: fetchInsights,
    getHighPriorityItems,
    getCTAConfig,
    hasHighPriorityItems,
  };
};

// Utility functions
const getSortedNBAItems = (items: NBAResponseItem[]): NBAResponseItem[] => {
  return [...items].sort((a, b) => a.priority - b.priority); // Sort by priority in ascending order (lower number = higher priority)
};
